// helix-exception: fork of Helix AlertBanner; keeps role="alert" for errors only and the section-link list Helix lacks (F-08)
import { Close, ExclamationCircle, InfoCircle } from 'flowbite-react-icons/outline'

export interface AlertSectionLink {
  id: string
  name: string
  onClick: () => void
}

export type AlertBannerVariant = 'info' | 'error'

interface AlertBannerProps {
  children?: React.ReactNode
  onClose?: () => void
  useInflectionIcon?: boolean
  leftIcon?: React.ReactNode
  introText?: string
  sectionLinks?: AlertSectionLink[]
  outroText?: string
  /** Visual severity. ``'info'`` keeps the existing brand-purple banner; ``'error'`` renders a red banner with an exclamation icon. Defaults to ``'info'``. */
  variant?: AlertBannerVariant
  /** Extra classes merged onto the banner container (e.g. to strengthen the border/shadow for a more prominent call-to-action). */
  className?: string
}

export function AlertBanner({
  children,
  onClose,
  useInflectionIcon = false,
  leftIcon,
  introText,
  sectionLinks,
  outroText = 'need your input.',
  variant = 'info',
  className = '',
}: AlertBannerProps) {
  const isError = variant === 'error'
  const containerClass = isError ? 'error-banner' : 'info-banner'
  const textColorClass = isError ? 'text-error-700' : 'text-text-fg-info'
  const closeColorClass = isError ? 'text-error-700' : 'text-text-fg-info'
  const formatSectionLinks = () => {
    if (!sectionLinks || sectionLinks.length === 0) return null

    return sectionLinks.map((section, index) => {
      const isLast = index === sectionLinks.length - 1
      const isSecondToLast = index === sectionLinks.length - 2

      return (
        <span key={section.id}>
          <button onClick={section.onClick} className="font-medium underline hover:text-brand-600 dark:hover:text-brand-400">
            {section.name}
          </button>
          {isSecondToLast && sectionLinks.length > 2 && ', and '}
          {isSecondToLast && sectionLinks.length === 2 && ' and '}
          {!isLast && !isSecondToLast && ', '}
        </span>
      )
    })
  }

  return (
    <div
      className={`${containerClass} !items-start flex-wrap !px-3 !pb-3 !pt-4 sm:flex-nowrap sm:!px-4 sm:!pb-4 sm:!pt-5 ${className}`}
      role={isError ? 'alert' : undefined}
      aria-live={isError ? 'polite' : undefined}
    >
      {leftIcon ? (
        leftIcon
      ) : isError ? (
        <ExclamationCircle className={`mt-0.5 h-4 w-4 shrink-0 ${textColorClass}`} />
      ) : useInflectionIcon ? (
        <InfoCircle className={`mt-0.5 h-4 w-4 shrink-0 ${textColorClass}`} />
      ) : (
        <InfoCircle className={`mt-0.5 h-4 w-4 shrink-0 ${textColorClass}`} />
      )}
      <p className={`min-w-0 flex-1 whitespace-normal break-words text-xs leading-5 sm:text-sm ${textColorClass}`}>
        {children ? (
          children
        ) : (
          <>
            {introText && <>{introText} </>}
            {formatSectionLinks()}
            {sectionLinks && sectionLinks.length > 0 && outroText && <> {outroText}</>}
          </>
        )}
      </p>
      {onClose && (
        <button onClick={onClose} aria-label="Dismiss alert" className={`mt-0.5 shrink-0 hover:opacity-70 ${closeColorClass}`}>
          <Close className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
